import React, {useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import http from 'Utils/http';
import { Box, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { setLoading } from 'Actions/loading';
import { Transition } from 'Shared/Transition';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import { useCreateUrl } from 'Hooks/useCreateUrl';

const FlowCopyForm = ({ id, flowName, visibleFlowForm, setVisibleFlowForm, needUpdateList, needUpdateCompanyFlows }) => {
    const navigate = useNavigate();
    // Новый flow всегда принадлежит пользователю
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { createUrl } = useCreateUrl();

    const onClose = () => {
        setVisibleFlowForm(false);
    };

    const {
        register,
        handleSubmit,
        formState: {errors},
    } = useForm();

    const updateFlow = useCallback(async (flowId, params) => {
        await http.post(createUrl(`/flow/${flowId}/update`), params).then((res) => {
            needUpdateList(true);
        });
    }, [createUrl, needUpdateList]);

    const onSubmit = (data) => {
        const params = {
            name: data.name,
            access: 'public',
        };
        dispatch(setLoading(true));
        http.post(createUrl(`/user/flow/${id}/copy`)).then(async (res) => {
            enqueueSnackbar('Copy Flow has been created', {
                action: CustomSnackbar,
                variant: 'success'
            });
            // Скрываем диалог сразу
            setVisibleFlowForm(false);
            
            // Переходим на страницу редактирования нового flow
            navigate(`/flow/${res.data.newFlow.id}`);
            
            await updateFlow(res.data.newFlow.id, params).then(() => {
                // Update company flows list if function is provided
                if (needUpdateCompanyFlows) {
                    needUpdateCompanyFlows();
                }
                setVisibleFlowForm(false);
            });
        }).catch((err) => {
            enqueueSnackbar(`${err.response.data.message}`, {
                action: CustomSnackbar,
                variant: 'error'
            });
        }).finally(() => {
            dispatch(setLoading(false));
        });
    };

    return (
        <Dialog
            open={visibleFlowForm}
            onClose={onClose}
            TransitionComponent={Transition}
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'flex-start',
                    paddingTop: '150px',
                },
                '& .MuiDialog-paper': {
                    minWidth: '450px',
                },
            }}
        >
            <DialogTitle
                className="h1"
                variant="h1"
                sx={{
                    paddingBottom: '6px',
                    textAlign: 'center',
                    color: '#3B465C',
                }}
            >
                Copy Flow
            </DialogTitle>
            <DialogContent>
                <Box
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <TextField
                        error={!!errors?.name}
                        helperText={errors?.name ? 'Flow name must be at most 255 characters' : ''}
                        fullWidth
                        id="name"
                        label="Flow name" placeholder={`Copy ${flowName}`}
                        name="name"
                        inputProps={{ maxLength: 255 }}
                        sx={{
                            marginTop: '10px',
                        }}
                        {...register('name', {
                            required: false,
                            maxLength: 255,
                        })}
                    />
                    <Typography
                        variant="caption"
                        sx={{ mt: 1, color: 'text.secondary', fontSize: '0.75rem' }}
                    >
                        By default: Copy {flowName}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions
                sx={{
                    padding: '16px 24px',
                }}
            >
                <Button onClick={onClose} variant="outlined">
                    Close
                </Button>
                <Button onClick={handleSubmit(onSubmit)} variant="contained">
                    Copy
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FlowCopyForm;
